import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  BookOpenCheck,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  Loader2,
  MessageSquareText,
  Plus,
  Send,
  XCircle,
} from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { normalizeRole } from '@/security/accessContract'
import { getAssistantWorkService } from '@/services/assistantWork'
import { getPracticeGovernanceService } from '@/services/practiceGovernance'
import type {
  AssistantPersona,
  AssistantWorkItem,
  AssistantWorkType,
  CreateResearchMaterialInput,
  ResearchMaterial,
} from '@/types/assistantWork.types'

const workLabels: Record<AssistantWorkType, string> = {
  technical_preparation: 'Technical preparation',
  technical_document: 'Technical document',
  client_query: 'Client query',
  filing_handoff: 'Filing handoff',
  research_assignment: 'Research assignment',
}

const statusClass: Record<string, string> = {
  assigned: 'bg-sky-100 text-sky-800',
  in_progress: 'bg-amber-100 text-amber-800',
  review_requested: 'bg-orange-100 text-orange-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
  draft: 'bg-slate-100 text-slate-700',
}

export default function AssistantWorkbenchPage() {
  const { user, hasPermission } = useAuth()
  const role = normalizeRole(user?.role)
  const manager = role === 'firm_owner' || role === 'firm_admin' || role === 'ca_partner'
  const technical = role === 'technical_assistant'
  const researcher = role === 'research_assistant'
  const canResearch = hasPermission('txh.assessment.research.read')
  const service = useMemo(() => getAssistantWorkService(), [])
  const governanceService = useMemo(() => getPracticeGovernanceService(), [])
  const [work, setWork] = useState<AssistantWorkItem[]>([])
  const [research, setResearch] = useState<ResearchMaterial[]>([])
  const [structure, setStructure] = useState({
    branches: [] as { id: string; name: string }[],
    teams: [] as { id: string; branchId: string; name: string }[],
    portfolios: [] as { id: string; branchId?: string; teamId?: string; name: string; ownerUserId: string; reviewerUserId?: string }[],
  })
  const [activePanel, setActivePanel] = useState<'work' | 'research'>(
    researcher ? 'research' : 'work',
  )
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [documents, setDocuments] = useState<Record<string, string>>({})
  const [assignment, setAssignment] = useState({
    assignedToUserId: '',
    customerId: '',
    assessmentId: '',
    assignedPersona: 'technical_assistant' as AssistantPersona,
    workType: 'technical_preparation' as AssistantWorkType,
    title: '',
    instructions: '',
    dueAt: '',
    branchId: '',
    teamId: '',
    portfolioId: '',
    portfolioOwnerUserId: '',
    reviewerId: '',
    riskLevel: 'standard',
    workValue: '',
  })
  const [researchForm, setResearchForm] = useState({
    customerId: '',
    assessmentId: '',
    title: '',
    analysis: '',
    advisoryDraft: '',
    sourceTitle: '',
    authority: '',
    sourceUrl: '',
    citationText: '',
    jurisdiction: 'India',
    assessmentYear: '',
    effectiveFrom: '',
  })

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [workItems, materials, governance] = await Promise.all([
        service.listWork(),
        canResearch ? service.listResearch() : Promise.resolve([]),
        manager ? governanceService.getSnapshot('firm') : Promise.resolve(null),
      ])
      setWork(workItems)
      setResearch(materials)
      if (governance) setStructure({ branches: governance.branches, teams: governance.teams, portfolios: governance.portfolios })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load assistant work')
    } finally {
      setLoading(false)
    }
  }, [canResearch, governanceService, manager, service])

  useEffect(() => {
    void load()
  }, [load])

  async function run(key: string, action: () => Promise<unknown>) {
    setBusy(key)
    setError(null)
    try {
      await action()
      await load()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The action could not be completed')
    } finally {
      setBusy(null)
    }
  }

  async function assignWork(event: FormEvent) {
    event.preventDefault()
    await run('assign', () => service.createWork({
      ...assignment,
      customerId: assignment.customerId || undefined,
      assessmentId: assignment.assessmentId || undefined,
      dueAt: assignment.dueAt ? new Date(assignment.dueAt).toISOString() : undefined,
      branchId: assignment.branchId || undefined,
      teamId: assignment.teamId || undefined,
      portfolioId: assignment.portfolioId || undefined,
      portfolioOwnerUserId: assignment.portfolioOwnerUserId || undefined,
      reviewerId: assignment.reviewerId || undefined,
      workValue: Number(assignment.workValue || 0),
    }))
    setAssignment((current) => ({ ...current, title: '', instructions: '' }))
  }

  async function createResearch(event: FormEvent) {
    event.preventDefault()
    const input: CreateResearchMaterialInput = {
      customerId: researchForm.customerId || undefined,
      assessmentId: researchForm.assessmentId || undefined,
      title: researchForm.title,
      analysis: researchForm.analysis,
      advisoryDraft: researchForm.advisoryDraft,
      citations: [{
        sourceTitle: researchForm.sourceTitle,
        authority: researchForm.authority,
        sourceUrl: researchForm.sourceUrl || undefined,
        citationText: researchForm.citationText,
      }],
      jurisdiction: researchForm.jurisdiction,
      assessmentYear: researchForm.assessmentYear || undefined,
      effectiveFrom: researchForm.effectiveFrom,
    }
    await run('research-create', () => service.createResearch(input))
    setResearchForm((current) => ({
      ...current, title: '', analysis: '', advisoryDraft: '',
      sourceTitle: '', sourceUrl: '', citationText: '',
    }))
  }

  const heading = researcher
    ? 'Research desk'
    : technical
      ? 'Technical work queue'
      : 'Assistant review room'

  if (loading) {
    return <div className="flex min-h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary-600" /></div>
  }

  return (
    <div className="space-y-6">
      <header className="overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">Segregated practice workflow</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">{heading}</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              {technical && 'Prepare assigned work, resolve queries, link technical documents, and hand off without filing authority.'}
              {researcher && 'Build sourced, effective-dated advisory research and send it to a partner for professional review.'}
              {manager && 'Assign work and accept or reject assistant submissions with an attributable decision trail.'}
            </p>
          </div>
          <div className="rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white">
            {work.filter((item) => item.status === 'review_requested').length} awaiting review
          </div>
        </div>
      </header>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div>}

      <div className="flex gap-2 border-b border-slate-200">
        <button className={`px-4 py-3 text-sm font-semibold ${activePanel === 'work' ? 'border-b-2 border-slate-950 text-slate-950' : 'text-slate-500'}`} onClick={() => setActivePanel('work')}>
          Work queue
        </button>
        {canResearch && (
          <button className={`px-4 py-3 text-sm font-semibold ${activePanel === 'research' ? 'border-b-2 border-slate-950 text-slate-950' : 'text-slate-500'}`} onClick={() => setActivePanel('research')}>
            Research library
          </button>
        )}
      </div>

      {activePanel === 'work' && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <section className="space-y-4">
            {work.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                <ClipboardList className="mx-auto h-10 w-10 text-slate-400" />
                <h2 className="mt-3 font-semibold text-slate-900">No work in this queue</h2>
                <p className="mt-1 text-sm text-slate-500">Assignments and review requests will appear here.</p>
              </div>
            )}
            {work.map((item) => (
              <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {item.workType === 'technical_document' ? <FileCheck2 className="h-5 w-5 text-cyan-700" /> : <MessageSquareText className="h-5 w-5 text-amber-700" />}
                      <h2 className="font-bold text-slate-950">{item.title}</h2>
                    </div>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{workLabels[item.workType]}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusClass[item.status] ?? 'bg-slate-100 text-slate-700'}`}>
                    {item.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-wide">
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">Risk: {item.riskLevel}</span>
                  {item.workValue > 0 && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">Value: ₹{item.workValue.toLocaleString('en-IN')}</span>}
                  {item.branchId && <span className="rounded-full bg-cyan-100 px-2.5 py-1 text-cyan-800">Branch: {structure.branches.find((branch) => branch.id === item.branchId)?.name ?? item.branchId}</span>}
                  {item.teamId && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-800">Team: {structure.teams.find((team) => team.id === item.teamId)?.name ?? item.teamId}</span>}
                </div>
                <p className="mt-4 text-sm text-slate-600">{item.instructions}</p>
                <div className="mt-4 grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
                  <span>Customer: {item.customerId ?? 'Linked by assessment'}</span>
                  <span>Persona: {item.assignedPersona.replace(/_/g, ' ')}</span>
                  <span>Due: {item.dueAt ? new Date(item.dueAt).toLocaleDateString() : 'Not set'}</span>
                </div>

                {!manager && !['review_requested', 'accepted'].includes(item.status) && (
                  <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">
                    {item.workType === 'technical_document' && (
                      <input className="input w-full" placeholder="Linked document UUID" value={documents[item.id] ?? item.artifactDocumentId ?? ''} onChange={(event) => setDocuments((current) => ({ ...current, [item.id]: event.target.value }))} />
                    )}
                    <textarea className="input min-h-24 w-full" placeholder={item.workType === 'client_query' ? 'Query response and resolution notes' : 'Preparation notes and handoff summary'} value={notes[item.id] ?? ''} onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))} />
                    <div className="flex flex-wrap gap-2">
                      <button className="btn btn-outline" disabled={busy === item.id} onClick={() => run(item.id, () => service.updateWork(item.id, {
                        artifactDocumentId: documents[item.id] || undefined,
                        responseText: notes[item.id] || undefined,
                        queryText: item.workType === 'client_query' ? notes[item.id] : undefined,
                      }))}>Save progress</button>
                      <button className="btn btn-primary flex items-center gap-2" disabled={!notes[item.id] || busy === item.id} onClick={() => run(item.id, () => service.submitWork(item.id, notes[item.id]))}>
                        <Send className="h-4 w-4" /> Hand off for review
                      </button>
                    </div>
                  </div>
                )}

                {manager && item.status === 'review_requested' && (
                  <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">
                    <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{item.handoffNotes}</p>
                    <textarea className="input min-h-20 w-full" placeholder="Review notes (required to reject)" value={notes[item.id] ?? ''} onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))} />
                    <div className="flex gap-2">
                      <button className="btn btn-primary flex items-center gap-2" onClick={() => run(item.id, () => service.reviewWork(item.id, 'accepted', notes[item.id]))}><CheckCircle2 className="h-4 w-4" />Accept</button>
                      <button className="btn btn-outline flex items-center gap-2 text-rose-700" disabled={!notes[item.id]} onClick={() => run(item.id, () => service.reviewWork(item.id, 'rejected', notes[item.id]))}><XCircle className="h-4 w-4" />Reject</button>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </section>

          {manager && hasPermission('txh.assessment.work.assign') && (
            <form onSubmit={assignWork} className="card card-elevated h-fit space-y-3 p-5">
              <div className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                <h2 className="text-base font-bold text-text-primary">Assign Work</h2>
              </div>
              <select className="input input-bordered w-full" value={assignment.assignedPersona} onChange={(event) => {
                const persona = event.target.value as AssistantPersona
                setAssignment((current) => ({ ...current, assignedPersona: persona, workType: persona === 'research_assistant' ? 'research_assignment' : 'technical_preparation' }))
              }}>
                <option value="technical_assistant">Technical Assistant</option>
                <option value="research_assistant">Research Assistant</option>
              </select>
              <select className="input input-bordered w-full" value={assignment.workType} onChange={(event) => setAssignment((current) => ({ ...current, workType: event.target.value as AssistantWorkType }))}>
                {(assignment.assignedPersona === 'technical_assistant' ? Object.keys(workLabels).filter((key) => key !== 'research_assignment') : ['research_assignment']).map((key) => <option key={key} value={key}>{workLabels[key as AssistantWorkType]}</option>)}
              </select>
              <input required className="input input-bordered w-full" placeholder="Assignee user UUID" value={assignment.assignedToUserId} onChange={(event) => setAssignment((current) => ({ ...current, assignedToUserId: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Customer ID" value={assignment.customerId} onChange={(event) => setAssignment((current) => ({ ...current, customerId: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Assessment UUID (optional)" value={assignment.assessmentId} onChange={(event) => setAssignment((current) => ({ ...current, assessmentId: event.target.value }))} />
              <select className="input input-bordered w-full" value={assignment.branchId} onChange={(event) => setAssignment((current) => ({ ...current, branchId: event.target.value, teamId: '', portfolioId: '' }))}>
                <option value="">Firm-wide / no branch</option>
                {structure.branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
              </select>
              <select className="input input-bordered w-full" value={assignment.teamId} onChange={(event) => setAssignment((current) => ({ ...current, teamId: event.target.value, portfolioId: '' }))}>
                <option value="">No team</option>
                {structure.teams.filter((team) => !assignment.branchId || team.branchId === assignment.branchId).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
              <select className="input input-bordered w-full" value={assignment.portfolioId} onChange={(event) => {
                const portfolio = structure.portfolios.find((item) => item.id === event.target.value)
                setAssignment((current) => ({ ...current, portfolioId: event.target.value, portfolioOwnerUserId: portfolio?.ownerUserId ?? current.portfolioOwnerUserId, reviewerId: portfolio?.reviewerUserId ?? current.reviewerId }))
              }}>
                <option value="">No portfolio</option>
                {structure.portfolios.filter((portfolio) => (!assignment.branchId || portfolio.branchId === assignment.branchId) && (!assignment.teamId || portfolio.teamId === assignment.teamId)).map((portfolio) => <option key={portfolio.id} value={portfolio.id}>{portfolio.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <select className="input input-bordered w-full" value={assignment.riskLevel} onChange={(event) => setAssignment((current) => ({ ...current, riskLevel: event.target.value }))}>
                  <option value="standard">Standard risk</option><option value="medium">Medium risk</option><option value="high">High risk</option><option value="critical">Critical risk</option>
                </select>
                <input className="input input-bordered w-full" type="number" min="0" placeholder="Work value ₹" value={assignment.workValue} onChange={(event) => setAssignment((current) => ({ ...current, workValue: event.target.value }))} />
              </div>
              <input className="input input-bordered w-full" placeholder="Portfolio owner user UUID" value={assignment.portfolioOwnerUserId} onChange={(event) => setAssignment((current) => ({ ...current, portfolioOwnerUserId: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Preferred reviewer user UUID" value={assignment.reviewerId} onChange={(event) => setAssignment((current) => ({ ...current, reviewerId: event.target.value }))} />
              <input required className="input input-bordered w-full" placeholder="Work title" value={assignment.title} onChange={(event) => setAssignment((current) => ({ ...current, title: event.target.value }))} />
              <textarea required className="input input-bordered min-h-24 w-full" placeholder="Instructions" value={assignment.instructions} onChange={(event) => setAssignment((current) => ({ ...current, instructions: event.target.value }))} />
              <input className="input input-bordered w-full" type="datetime-local" value={assignment.dueAt} onChange={(event) => setAssignment((current) => ({ ...current, dueAt: event.target.value }))} />
              <button className="btn btn-primary w-full" disabled={busy === 'assign'}>{busy === 'assign' ? 'Assigning...' : 'Create Assignment'}</button>
            </form>
          )}
        </div>
      )}

      {activePanel === 'research' && canResearch && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <section className="space-y-4">
            {research.map((item) => (
              <article key={item.id} className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex gap-3"><BookOpenCheck className="mt-1 h-5 w-5 text-cyan-700" /><div><h2 className="font-bold text-slate-950">{item.title}</h2><p className="text-xs text-slate-500">{item.jurisdiction} · effective {item.effectiveFrom}</p></div></div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusClass[item.status] ?? 'bg-slate-100'}`}>{item.status.replace(/_/g, ' ')}</span>
                </div>
                <p className="mt-4 text-sm text-slate-700">{item.analysis}</p>
                <div className="mt-4 rounded-xl border-l-4 border-cyan-500 bg-cyan-50 p-4 text-sm text-slate-700">{item.advisoryDraft}</div>
                <div className="mt-4 space-y-2">
                  {item.citations.map((citation, index) => (
                    <div key={index} className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                      <strong>{citation.authority}: {citation.sourceTitle}</strong>
                      <p className="mt-1">{citation.citationText}</p>
                      {citation.sourceUrl && <a className="mt-1 block text-cyan-700 underline" href={citation.sourceUrl} target="_blank" rel="noreferrer">Open source</a>}
                    </div>
                  ))}
                </div>
                {researcher && ['draft', 'rejected'].includes(item.status) && (
                  <button className="btn btn-primary mt-4 flex items-center gap-2" onClick={() => run(`research-${item.id}`, () => service.submitResearch(item.id))}><Send className="h-4 w-4" />Request partner review</button>
                )}
                {manager && item.status === 'review_requested' && (
                  <div className="mt-4 space-y-2">
                    <textarea className="input min-h-20 w-full" placeholder="Review notes (required to reject)" value={notes[item.id] ?? ''} onChange={(event) => setNotes((current) => ({ ...current, [item.id]: event.target.value }))} />
                    <div className="flex gap-2">
                      <button className="btn btn-primary" onClick={() => run(`research-${item.id}`, () => service.reviewResearch(item.id, 'accepted', notes[item.id]))}>Accept research</button>
                      <button className="btn btn-outline text-rose-700" disabled={!notes[item.id]} onClick={() => run(`research-${item.id}`, () => service.reviewResearch(item.id, 'rejected', notes[item.id]))}>Reject</button>
                    </div>
                  </div>
                )}
              </article>
            ))}
            {research.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">No research material in this workspace.</div>}
          </section>

          {researcher && hasPermission('txh.assessment.research.create') && (
            <form onSubmit={createResearch} className="card card-elevated h-fit space-y-3 p-5">
              <h2 className="text-base font-bold text-text-primary">New Sourced Research</h2>
              <input className="input input-bordered w-full" placeholder="Customer ID" value={researchForm.customerId} onChange={(event) => setResearchForm((current) => ({ ...current, customerId: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Assessment UUID (optional)" value={researchForm.assessmentId} onChange={(event) => setResearchForm((current) => ({ ...current, assessmentId: event.target.value }))} />
              <input required className="input input-bordered w-full" placeholder="Research title" value={researchForm.title} onChange={(event) => setResearchForm((current) => ({ ...current, title: event.target.value }))} />
              <textarea required className="input input-bordered min-h-20 w-full" placeholder="Analysis" value={researchForm.analysis} onChange={(event) => setResearchForm((current) => ({ ...current, analysis: event.target.value }))} />
              <textarea required className="input input-bordered min-h-20 w-full" placeholder="Advisory draft" value={researchForm.advisoryDraft} onChange={(event) => setResearchForm((current) => ({ ...current, advisoryDraft: event.target.value }))} />
              <div className="border-t border-border-light dark:border-border-dark pt-3 text-xs font-bold uppercase tracking-wide text-text-secondary">Source citation</div>
              <input required className="input input-bordered w-full" placeholder="Source title" value={researchForm.sourceTitle} onChange={(event) => setResearchForm((current) => ({ ...current, sourceTitle: event.target.value }))} />
              <input required className="input input-bordered w-full" placeholder="Authority" value={researchForm.authority} onChange={(event) => setResearchForm((current) => ({ ...current, authority: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Source URL" value={researchForm.sourceUrl} onChange={(event) => setResearchForm((current) => ({ ...current, sourceUrl: event.target.value }))} />
              <textarea required className="input input-bordered min-h-16 w-full" placeholder="Citation / clause" value={researchForm.citationText} onChange={(event) => setResearchForm((current) => ({ ...current, citationText: event.target.value }))} />
              <input required className="input input-bordered w-full" placeholder="Jurisdiction" value={researchForm.jurisdiction} onChange={(event) => setResearchForm((current) => ({ ...current, jurisdiction: event.target.value }))} />
              <input className="input input-bordered w-full" placeholder="Assessment year" value={researchForm.assessmentYear} onChange={(event) => setResearchForm((current) => ({ ...current, assessmentYear: event.target.value }))} />
              <label className="block text-xs text-text-secondary">Effective from<input required type="date" className="input input-bordered mt-1 w-full" value={researchForm.effectiveFrom} onChange={(event) => setResearchForm((current) => ({ ...current, effectiveFrom: event.target.value }))} /></label>
              <button className="btn btn-primary w-full" disabled={busy === 'research-create'}>Save Research Draft</button>
            </form>
          )}
        </div>
      )}
    </div>
  )
}
